import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CSpinner,
  CRow,
  CCol,
  CBadge,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CAlert
} from "@coreui/react";
import { fetchSkillById, apiService } from "../services/api";

const SkillDetail = () => {
  const [skillInfo, setSkillInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get skillId and stage from URL query params first so we can use it in the hooks below
  const queryParams = new URLSearchParams(location.search);
  const skillId = queryParams.get("id");
  const stage = queryParams.get("stage") || "development";
  
  // Publishing workflow state - persisted to localStorage when changed
  const [publishingStep, setPublishingStep] = useState(() => {
    if (!skillId) return 1;
    return parseInt(localStorage.getItem(`skill_${skillId}_publishingStep`) || '1');
  });
  const [accessToken, setAccessToken] = useState(() => {
    if (!skillId) return '';
    return localStorage.getItem(`skill_${skillId}_accessToken`) || '';
  });
  const [publishingStatus, setPublishingStatus] = useState(() => {
    if (!skillId) return '';
    return localStorage.getItem(`skill_${skillId}_publishingStatus`) || '';
  });
  const [certificationStatus, setCertificationStatus] = useState(() => {
    if (!skillId) return '';
    return localStorage.getItem(`skill_${skillId}_certificationStatus`) || '';
  });
  const [isPublished, setIsPublished] = useState(() => {
    if (!skillId) return false;
    return localStorage.getItem(`skill_${skillId}_isPublished`) === 'true';
  });
  const [publishingError, setPublishingError] = useState('');
  const [publishingSuccess, setPublishingSuccess] = useState('');
  const [isPublishingLoading, setIsPublishingLoading] = useState(false);
  const [buildCheckInterval, setBuildCheckInterval] = useState(null);
  const [buildProgress, setBuildProgress] = useState(() => {
    if (!skillId) return 0;
    return parseInt(localStorage.getItem(`skill_${skillId}_buildProgress`) || '0');
  });
  const [buildStatus, setBuildStatus] = useState(() => {
    if (!skillId) return '';
    return localStorage.getItem(`skill_${skillId}_buildStatus`) || ''; // 'pending', 'in_progress', 'succeeded', 'failed'
  });
  const [showPublishing, setShowPublishing] = useState(() => {
    if (!skillId) return false;
    return localStorage.getItem(`skill_${skillId}_showPublishing`) === 'true';
  });
  const [lastStatusCheck, setLastStatusCheck] = useState(0); // Throttle API calls
  const [publicationCheckStatus, setPublicationCheckStatus] = useState(() => {
    if (!skillId) return null;
    return localStorage.getItem(`skill_${skillId}_publicationCheckStatus`) || null;
  });
  const [publishesAtDate, setPublishesAtDate] = useState(() => {
    if (!skillId) return null;
    return localStorage.getItem(`skill_${skillId}_publishesAtDate`) || null;
  });

  const fetchSkillDetails = React.useCallback(async () => {
    if (!skillId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await fetchSkillById(skillId, stage);
      setSkillInfo(data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch skill details");
    } finally {
      setLoading(false);
    }
  }, [skillId, stage]);

  // Persist state changes to localStorage
  useEffect(() => {
    if (skillId) {
      localStorage.setItem(`skill_${skillId}_publishingStep`, publishingStep);
      localStorage.setItem(`skill_${skillId}_accessToken`, accessToken);
      localStorage.setItem(`skill_${skillId}_publishingStatus`, publishingStatus);
      localStorage.setItem(`skill_${skillId}_certificationStatus`, certificationStatus);
      localStorage.setItem(`skill_${skillId}_isPublished`, isPublished);
      localStorage.setItem(`skill_${skillId}_buildProgress`, buildProgress);
      localStorage.setItem(`skill_${skillId}_buildStatus`, buildStatus);
      localStorage.setItem(`skill_${skillId}_showPublishing`, showPublishing);
      if (publicationCheckStatus) localStorage.setItem(`skill_${skillId}_publicationCheckStatus`, publicationCheckStatus);
      if (publishesAtDate) localStorage.setItem(`skill_${skillId}_publishesAtDate`, publishesAtDate);
    }
  }, [skillId, publishingStep, accessToken, publishingStatus, certificationStatus, isPublished, buildProgress, buildStatus, showPublishing, publicationCheckStatus, publishesAtDate]);

  // Initial skill details fetch
  useEffect(() => {
    fetchSkillDetails();
  }, [skillId, fetchSkillDetails]);

  const handleCheckStatus = async (isAutoDetect = false) => {
    // Throttle API calls to avoid excessive requests
    const now = Date.now();
    if (now - lastStatusCheck < 2000 && !isAutoDetect) { // 2-second throttle except for auto-detection
      console.log('Throttling status check');
      return;
    }
    setLastStatusCheck(now);
    
    if (!isAutoDetect) {
      setIsPublishingLoading(true);
      setPublishingError('');
    }
    
    try {
      const statusResponse = await apiService.getSkillStatus(accessToken, skillId);
      
      // Store the skill status and update progress indicators
      setPublishingStatus(statusResponse.status || 'UNKNOWN');
      setCertificationStatus(statusResponse.certificationStatus || 'NOT_CERTIFIED');
      
      // Update published status if available
      if (statusResponse.isPublished !== undefined) {
        setIsPublished(statusResponse.isPublished);
      }
      
      // Check if we need to auto-detect the current step based on the status
      if (isAutoDetect) {
        autoDetectCurrentStep(statusResponse);
      }
      
      // Success for general status check!
      if (!isAutoDetect) {
        // If manually refreshing, clear previous specific success/error related to publication check
        setPublishingSuccess(''); 
      }

      // Now, if relevant, check detailed publication status
      if (publishingStep === 6 || statusResponse.status === 'SUCCEEDED' || statusResponse.status === 'LIVE' || publishingStatus === 'PUBLISHED' || publishingStatus === 'Live') {
        try {
          console.log('Fetching detailed publication status...');
          const pubStatusResponse = await apiService.getPublicationStatus(skillId);
          setPublicationCheckStatus(pubStatusResponse.status || 'UNKNOWN');
          setPublishesAtDate(pubStatusResponse.publishesAt || null);

          if (!isAutoDetect) { // Only show detailed messages on manual refresh or explicit step actions
            if (pubStatusResponse.status === 'SUCCEEDED') {
              setPublishingSuccess('Skill successfully published! Status: SUCCEEDED.');
              setIsPublished(true);
            } else if (pubStatusResponse.status === 'FAILED') {
              setPublishingError(`Publication failed. Status: FAILED. Last attempted: ${pubStatusResponse.publishesAt || 'N/A'}. Check Amazon Developer Console.`);
              setIsPublished(false);
            } else if (pubStatusResponse.status === 'IN_PROGRESS') {
              setPublishingSuccess(`Publication is IN_PROGRESS. Expected around: ${pubStatusResponse.publishesAt ? new Date(pubStatusResponse.publishesAt).toLocaleString() : 'N/A'}.`);
            } else if (pubStatusResponse.status === 'SCHEDULED') {
              setPublishingSuccess(`Publication is SCHEDULED for: ${pubStatusResponse.publishesAt ? new Date(pubStatusResponse.publishesAt).toLocaleString() : 'N/A'}.`);
            } else {
              setPublishingSuccess(`Publication status: ${pubStatusResponse.status}. Published at: ${pubStatusResponse.publishesAt || 'N/A'}`);
            }
          }
        } catch (pubErr) {
          console.error('Detailed publication status check error:', pubErr);
          if (!isAutoDetect) {
            setPublishingError(`Failed to get detailed publication status: ${pubErr.message}`);
          }
          // Keep the general status if detailed one fails
          setPublicationCheckStatus(null);
          setPublishesAtDate(null);
        }
      }

      setIsPublishingLoading(false);
      if (!isAutoDetect && !publishingError && !publishingSuccess) {
        // If no specific error/success was set by publication check, ensure general error is cleared
        setPublishingError(''); 
      }
    } catch (err) {
      if (!isAutoDetect) {
        setPublishingError(`Failed to get skill status: ${err.message}`);
      }
      console.error('Status check error:', err);
      setIsPublishingLoading(false);
      return null;
    } finally {
      if (!isAutoDetect) {
        setIsPublishingLoading(false);
      }
    }
  };
  
  // Auto-detect which step we should be on based on skill status
  const autoDetectCurrentStep = (statusResponse) => {
    if (!statusResponse) return;
    
    // If the skill is already published, we've completed all steps
    if (statusResponse.isPublished) {
      setPublishingStep(6); // All steps done including publishing
      setPublishingSuccess('Skill is published!');
      return;
    }
    
    // If the skill is certified but not published, we're at the publication step
    if (statusResponse.isCertified && !statusResponse.isPublished) {
      setPublishingStep(5); // Ready for publication
      setPublishingSuccess('Skill is certified and ready for publication!');
      return;
    }
    
    // If we don't have an access token, we're at step 1
    if (!accessToken) {
      setPublishingStep(1);
      return;
    }
    
    // We now use simplified status checks: just check if testing is enabled and if interaction model exists
    const certStatus = statusResponse.certificationStatus || 'NOT_CERTIFIED';
    const interactionModelExists = statusResponse.interactionModelExists || false;
    const isTestingEnabled = statusResponse.isTestingEnabled || false;
    const status = statusResponse.status || '';
    
    // Log the status for debugging
    console.log('Skill status check (simplified):', {
      interactionModelExists,
      isTestingEnabled,
      certStatus,
      status
    });
    
    // Step 5: Certification
    // Check if skill has been submitted for certification
    if (certStatus !== 'NOT_CERTIFIED') {
      setPublishingStep(5);
      if (certStatus === 'CERTIFIED') {
        setPublishingSuccess('Skill is certified and ready for publication!');
        setCertificationStatus('CERTIFIED');
      } else if (certStatus === 'IN_PROGRESS') {
        setPublishingSuccess('Certification is in progress. Please check back later.');
        setCertificationStatus('IN_PROGRESS');
      } else {
        setPublishingSuccess(`Certification status: ${certStatus}`);
        setCertificationStatus(certStatus);
      }
      return;
    }
    
    // Step 4: Testing enabled
    if (isTestingEnabled) {
      setPublishingStep(4);
      setBuildStatus('succeeded');
      setBuildProgress(100);
      setPublishingSuccess('Testing is enabled. You can now submit for certification.');
      return;
    }
    
    // If not testing enabled but interaction model exists, we're at step 3
    if (interactionModelExists) {
      setPublishingStep(3);
      setBuildStatus('succeeded');
      setBuildProgress(100);
      setPublishingSuccess('Interaction model exists. You can now enable testing.');
      return;
    }
    
    // If we have a skill ID but no interaction model, we're at step 2
    if (skillId) {
      setPublishingStep(2);
      setBuildStatus('pending');
      setBuildProgress(0);
      setPublishingSuccess('Interaction model needs to be built. Please build the model.');
      return;
    }
    
    // Default to step 1
    setPublishingStep(1);
  };
  
  // Auto-detect the current state on initial load
  useEffect(() => {
    const detectCurrentState = async () => {
      if (skillId && accessToken && !isPublishingLoading) {
        try {
          await handleCheckStatus(true);
        } catch (err) {
          console.error('Failed to auto-detect state:', err);
        }
      }
    };
    
    // Only run once on initial load if we have token and skillId
    if (skillId && accessToken) {
      detectCurrentState();
    }
  }, [skillId, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBackClick = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <CSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <CCard className="mb-4">
        <CCardBody>
          <div className="alert alert-danger">{error}</div>
          <CButton color="secondary" onClick={handleBackClick}>
            Back to Skills List
          </CButton>
        </CCardBody>
      </CCard>
    );
  }

  if (!skillInfo) {
    return (
      <CCard className="mb-4">
        <CCardBody>
          <div className="alert alert-warning">No skill information found</div>
          <CButton color="secondary" onClick={handleBackClick}>
            Back to Skills List
          </CButton>
        </CCardBody>
      </CCard>
    );
  }

  // Extract fields for display, handling potential missing data
  const name = skillInfo.name || skillInfo.manifest?.publishingInformation?.locales?.["en-US"]?.name || "Unnamed Skill";
  const description = skillInfo.description || skillInfo.manifest?.publishingInformation?.locales?.["en-US"]?.description || "";
  const shortDescription = skillInfo.shortDescription || skillInfo.manifest?.publishingInformation?.locales?.["en-US"]?.summary || "";
  // Use publication status if available, otherwise fall back to stage
  const status = skillInfo.publicationStatus || 
                (skillInfo.isPublished ? 'PUBLISHED' : 
                (skillInfo.isCertified ? 'CERTIFIED' : 
                (skillInfo.manifest?.stage || "Unknown")));
  const invocationName = skillInfo.invocationName || skillInfo.manifest?.apis?.custom?.endpoint?.uri || "";
  const examplePhrases = skillInfo.examplePhrases || skillInfo.manifest?.publishingInformation?.locales?.["en-US"]?.examplePhrases || [];
  const keywords = skillInfo.keywords || skillInfo.manifest?.publishingInformation?.locales?.["en-US"]?.keywords || [];
  const category = skillInfo.category || skillInfo.manifest?.publishingInformation?.category || "";
  const privacyPolicyUrl = skillInfo.privacyPolicyUrl || skillInfo.manifest?.privacyAndCompliance?.locales?.["en-US"]?.privacyPolicyUrl || "";
  const smallIconUrl = skillInfo.smallIconUrl || skillInfo.manifest?.publishingInformation?.locales?.["en-US"]?.smallIconUri || "";
  const largeIconUrl = skillInfo.largeIconUrl || skillInfo.manifest?.publishingInformation?.locales?.["en-US"]?.largeIconUri || "";

  // Handle publishing a certified skill
  const handlePublishSkill = async () => {
    setIsPublishingLoading(true);
    setPublishingError('');
    setPublishingSuccess('');
    
    try {
      // Call the API to publish the skill
      const response = await apiService.publishSkill(accessToken, skillId);
      
      // Update status and move to the next step
      setPublishingStatus('PUBLISHING');
      setPublishingStep(6); // Move to completion step after publishing
      setPublishingSuccess(`Publication initiated! ${response.message || ''} Your skill will now be processed for publication, which may take anywhere from 15 minutes up to 24 hours to complete.`);
      
      // Check status after a short delay
      setTimeout(() => handleCheckStatus(true), 2000);
    } catch (error) {
      console.error('Error publishing skill:', error);
      setPublishingError(error.response?.data?.message || error.response?.data?.error || 'Failed to publish skill');
    } finally {
      setIsPublishingLoading(false);
    }
  };
  
  // Publishing workflow handlers
  const handleAuthenticateForPublishing = async () => {
    setIsPublishingLoading(true);
    setPublishingError('');
    setPublishingSuccess('');
    
    try {
      const response = await apiService.authenticateWithAmazon();
      setAccessToken(response.accessToken);
      setPublishingSuccess('Authentication successful! Ready to proceed with publishing steps.');
      setPublishingStep(2); // Skip directly to build model step
    } catch (err) {
      setPublishingError(`Authentication failed: ${err.message}`);
    } finally {
      setIsPublishingLoading(false);
    }
  };

  const handleBuildModel = async () => {
    setIsPublishingLoading(true);
    setPublishingError('');
    setPublishingSuccess('');
    setBuildProgress(0);
    setBuildStatus('pending');
    
    try {
      // Call API to build the interaction model
      // Check if there's a stored invocation name for this skill
      const storedInvocationName = localStorage.getItem(`skill_${skillId}_invocationName`);
      console.log(`🔍 Building interaction model for skill ${skillId}`);
      console.log(`📦 Stored invocation name from localStorage:`, storedInvocationName || '(none)');
      console.log(`📦 Type of stored invocation name:`, typeof storedInvocationName);
      console.log(`📦 Length of stored invocation name:`, storedInvocationName ? storedInvocationName.length : 0);
      
      // Use the stored invocation name if it exists and is not empty, otherwise use undefined
      const invocationNameToSend = (storedInvocationName && storedInvocationName.trim().length > 0) 
        ? storedInvocationName.trim() 
        : undefined;
      
      console.log(`📦 Invocation name to send:`, invocationNameToSend || '(none/undefined)');
      
      const response = await apiService.buildInteractionModel(accessToken, skillId, invocationNameToSend);
      console.log(`✅ Build interaction model response:`, response);
      setPublishingSuccess('Interaction model build initiated. Please wait while the build completes...');
      
      // Set the publishing status from the response
      if (response.status) {
        setPublishingStatus(response.status);
      }
      
      // Clear any existing interval
      if (buildCheckInterval) {
        clearInterval(buildCheckInterval);
      }
      
      // Start checking if interaction model exists
      setBuildStatus('in_progress');
      let progress = 10;
      setBuildProgress(progress);
      
      const maxChecks = 24; // Maximum number of checks (24 x 5 seconds = 2 minutes)
      let checkCount = 0;
      
      const intervalId = setInterval(async () => {
        checkCount++;
        
        try {
          // Increment progress on each check
          progress = Math.min(progress + 5, 95); 
          setBuildProgress(progress);
          
          // Check if the interaction model exists now
          const statusResponse = await apiService.getSkillStatus(accessToken, skillId);
          
          // Log simplified status for debugging
          console.log('Build status check (simplified):', {
            interactionModelExists: statusResponse.interactionModelExists,
            checkCount,
            progress
          });
          
          // If the interaction model exists, we're ready to move forward
          if (statusResponse.interactionModelExists) {
            clearInterval(intervalId);
            setBuildCheckInterval(null);
            setBuildProgress(100);
            setBuildStatus('succeeded');
            setPublishingSuccess('Interaction model created successfully! You can now enable testing.');
            setPublishingStep(3);
            setIsPublishingLoading(false);
            return;
          }
          
          // If we've checked enough times without finding an interaction model, stop
          if (checkCount >= maxChecks) {
            clearInterval(intervalId);
            setBuildCheckInterval(null);
            setBuildStatus('unknown');
            setPublishingError('Could not confirm if interaction model was created. Please check in the Amazon Developer Console.');
            setIsPublishingLoading(false);
          }
        } catch (checkErr) {
          console.error('Error checking interaction model:', checkErr);
          
          // If we've been checking for a while, stop after enough errors
          if (checkCount >= 12) { // After 1 minute (12 checks at 5 seconds each)
            clearInterval(intervalId);
            setBuildCheckInterval(null);
            setPublishingError('Unable to confirm interaction model status. You may need to try again.');
            setIsPublishingLoading(false);
          }
        }
      }, 5000); // Check every 5 seconds
      
      setBuildCheckInterval(intervalId);
    } catch (err) {
      console.error('Build model error:', err);
      setPublishingError(`Building interaction model failed: ${err.response?.data?.error || err.message}`);
      setBuildStatus('failed');
      setIsPublishingLoading(false);
    }
  };

  const handleEnableTesting = async () => {
    setIsPublishingLoading(true);
    setPublishingError('');
    setPublishingSuccess('');
    
    try {
      // First check if the interaction model exists and testing is already enabled
      const statusResponse = await apiService.getSkillStatus(accessToken, skillId);
      const interactionModelExists = statusResponse.interactionModelExists || false;
      const isTestingEnabled = statusResponse.isTestingEnabled || false;
      
      // Log status for debugging
      console.log('Enable testing status check (simplified):', {
        interactionModelExists,
        isTestingEnabled
      });
      
      // If testing is already enabled, no need to proceed
      if (isTestingEnabled) {
        setPublishingSuccess('Testing is already enabled for this skill!');
        setPublishingStep(4);
        setBuildStatus('succeeded');
        setBuildProgress(100);
        setIsPublishingLoading(false);
        return;
      }
      
      // If the interaction model doesn't exist, we need to build it first
      if (!interactionModelExists) {
        setPublishingError('The interaction model does not exist yet. Please go back to Step 2 and build the model first.');
        setPublishingStep(2);
        setIsPublishingLoading(false);
        return;
      }
      
      // Try enabling testing with a retry mechanism
      let attempt = 1;
      let maxAttempts = 5; // Maximum number of retry attempts
      let success = false;
      
      setPublishingSuccess('Starting to enable testing. This may take several attempts...');
      
      while (attempt <= maxAttempts && !success) {
        try {
          setPublishingSuccess(`Enabling testing attempt ${attempt} of ${maxAttempts}...`);
          await apiService.enableTesting(accessToken, skillId);
          
          // After successful enablement, fetch status again to confirm
          const updatedStatus = await apiService.getSkillStatus(accessToken, skillId);
          if (updatedStatus.isTestingEnabled) {
            setPublishingSuccess('Testing enabled successfully!');
            setPublishingStep(4);
            setBuildStatus('succeeded');
            setBuildProgress(100);
            success = true;
          } else {
            // We'll try again if this isn't the last attempt
            if (attempt < maxAttempts) {
              const waitTime = 3000;
              setPublishingSuccess(`Testing not confirmed as enabled yet. Waiting ${waitTime/1000} seconds before next attempt...`);
              await new Promise(resolve => setTimeout(resolve, waitTime));
            }
            attempt++;
          }
        } catch (enableErr) {
          console.error(`Enable testing attempt ${attempt} failed:`, enableErr);
          
          // Extract error message for more helpful feedback
          const errorData = enableErr.response?.data;
          const violationMessage = errorData?.violations?.[0]?.message || '';
          
          // Determine appropriate wait time based on error
          let waitTime = 3000; // Default 3 seconds
          let messagePrefix = '';
          
          if (violationMessage.includes('interaction model')) {
            waitTime = attempt * 3000; // Longer waits for model issues
            messagePrefix = 'The interaction model is still being processed.';
          } else if (violationMessage.includes('skill is not ready')) {
            waitTime = attempt * 2000;
            messagePrefix = 'Skill is not ready for enablement yet.';
          } else {
            waitTime = 2000;
            messagePrefix = 'Encountered an issue.';
          }
          
          // Only wait if this isn't the last attempt
          if (attempt < maxAttempts) {
            setPublishingSuccess(`${messagePrefix} Waiting ${waitTime/1000} seconds before next attempt (${attempt}/${maxAttempts})...`);
            await new Promise(resolve => setTimeout(resolve, waitTime));
          }
          
          attempt++;
        }
      }
      
      // After all attempts, if still not successful
      if (!success) {
        setPublishingError(`Failed to enable testing after ${maxAttempts} attempts. The skill may not be ready yet. Wait a few minutes and try again, or try rebuilding the interaction model.`);
      }
    } catch (err) {
      setPublishingError(`Error enabling testing: ${err.message}`);
    } finally {
      setIsPublishingLoading(false);
    }
  };

  const handleSubmitForCertification = async () => {
    setIsPublishingLoading(true);
    setPublishingError('');
    setPublishingSuccess('');
    
    try {
      // Fetch current status with our simplified approach
      const statusResponse = await apiService.getSkillStatus(accessToken, skillId);
      const interactionModelExists = statusResponse.interactionModelExists || false;
      const isTestingEnabled = statusResponse.isTestingEnabled || false;
      
      // Log status for debugging
      console.log('Certification status check (simplified):', {
        interactionModelExists,
        isTestingEnabled,
        certificationStatus: statusResponse.certificationStatus
      });
      
      // Check if interaction model exists
      if (!interactionModelExists) {
        setPublishingError('The interaction model must exist before submitting for certification. Please go back to Step 2.');
        setPublishingStep(2);
        setIsPublishingLoading(false);
        return;
      }
      
      // Check if testing is enabled
      if (!isTestingEnabled) {
        setPublishingError('Testing must be enabled before submitting for certification. Please go back to Step 3 and enable testing first.');
        setPublishingStep(3);
        setIsPublishingLoading(false);
        return;
      }
      
      // Check if already certified
      if (statusResponse.isCertified) {
        setPublishingSuccess('This skill is already certified! No need to submit for certification again.');
        setPublishingStatus('CERTIFIED');
        setCertificationStatus('CERTIFIED');
        setPublishingStep(5);
        setIsPublishingLoading(false);
        return;
      } 
      
      // Check if already in certification process
      if (statusResponse.certificationStatus && 
          statusResponse.certificationStatus !== 'NOT_CERTIFIED') {
        setPublishingSuccess(`This skill is already in the certification process with status: ${statusResponse.certificationStatus}`);
        setPublishingStatus(statusResponse.certificationStatus);
        setCertificationStatus(statusResponse.certificationStatus);
        setPublishingStep(5);
        setIsPublishingLoading(false);
        return;
      }
      
      // Proceed with submission if not certified and not in process
      await apiService.submitForCertification(accessToken, skillId);
      
      // Check status again after submission
      const updatedStatus = await apiService.getSkillStatus(accessToken, skillId);
      setPublishingSuccess('Skill submitted for certification successfully!');
      setPublishingStatus(updatedStatus.certificationStatus || 'Submitted for certification');
      setCertificationStatus(updatedStatus.certificationStatus || 'IN_PROGRESS');
      setPublishingStep(5);
    } catch (err) {
      // Check for specific error about already being certified
      const errorMsg = err.message || '';
      if (errorMsg.includes('Certified') || errorMsg.includes('certified')) {
        setPublishingSuccess('This skill already has a certified version. You can view it in the Amazon Developer Console.');
        setPublishingStatus('CERTIFIED');
        setCertificationStatus('CERTIFIED');
        setPublishingStep(5);
      } else {
        setPublishingError(`Submission for certification failed: ${errorMsg}`);
      }
    } finally {
      setIsPublishingLoading(false);
    }
  };
  
  const renderPublishingStatusIndicator = () => {
    if (isPublishingLoading) {
      return (
        <div className="mt-3">
          <CSpinner color="primary" className="me-2" />
          {buildStatus === 'in_progress' && (
            <div className="mt-2">
              <div className="progress">
                <div 
                  className="progress-bar progress-bar-striped progress-bar-animated" 
                  role="progressbar" 
                  style={{ width: `${buildProgress}%` }} 
                  aria-valuenow={buildProgress} 
                  aria-valuemin="0" 
                  aria-valuemax="100"
                >
                  {buildProgress}%
                </div>
              </div>
              <small className="text-muted">Building interaction model... Please wait.</small>
            </div>
          )}
        </div>
      );
    }
    if (publishingError) {
      return <CAlert color="danger" className="mt-3">{publishingError}</CAlert>;
    }
    if (publishingSuccess) {
      return <CAlert color="success" className="mt-3">{publishingSuccess}</CAlert>;
    }
    return null;
  };

  const getBadgeColor = (status) => {
    if (!status) return 'secondary';
    const lowerStatus = String(status).toLowerCase(); // Ensure status is a string before calling toLowerCase
    switch (lowerStatus) {
      case 'live':
      case 'succeeded':
      case 'certified':
      case 'ready_for_publication':
        return 'success';
      case 'in_progress':
      case 'certification_in_progress':
      case 'build_in_progress':
      case 'publishing_in_progress': // Added for completeness
        return 'info';
      case 'failed':
      case 'build_failed':
      case 'certification_failed': // Added for completeness
      case 'publishing_failed': // Added for completeness
        return 'danger';
      case 'development':
      case 'not_certified':
        return 'warning';
      default:
        return 'primary'; // For 'UNKNOWN' or other statuses
    }
  };

  const getBadgeColorForPublication = (status) => {
    if (!status) return 'secondary';
    status = status.toUpperCase(); // Publication statuses are usually uppercase
    switch (status) {
      case 'SUCCEEDED':
        return 'success';
      case 'FAILED':
        return 'danger';
      case 'IN_PROGRESS':
        return 'info';
      case 'SCHEDULED':
        return 'warning';
      case 'PENDING_APPROVAL': // Example of another possible status
        return 'primary';
      default:
        return 'secondary';
    }
  };
  
  const togglePublishingSection = () => {
    if (!showPublishing) {
      // Initialize publishing flow when showing it
      handleAuthenticateForPublishing();
    }
    setShowPublishing(!showPublishing);
  };
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>{name}</h2>
        <div>
          <CButton color="primary" className="mx-2" onClick={togglePublishingSection}>
            {showPublishing ? 'Withdraw Publishing' : 'Publish Skill'}
          </CButton>
          <CButton color="secondary" onClick={handleBackClick}>
            Back to Skills List
          </CButton>
        </div>
      </div>

      {showPublishing && (
        <CCard className="mb-4">
          <CCardHeader>
            <h4>Publish Alexa Skill: {name}</h4>
          </CCardHeader>
          <CCardBody>
            {/* Step Progress Bar */}
            <CNav variant="tabs">
              {[1, 2, 3, 4, 5, 6].map(step => (
                <CNavItem key={step}>
                  <CNavLink 
                    active={publishingStep === step}
                    onClick={() => publishingStep >= step && setPublishingStep(step)}
                    disabled={step > Math.max(publishingStep, 1) || isPublishingLoading}
                  >
                    {step === 1 ? 'Initialize' : 
                      step === 2 ? 'Build Model' : 
                      step === 3 ? 'Enable Testing' : 
                      step === 4 ? 'Submit for Certification' : 
                      step === 5 ? 'Publish Skill' : 'Status'}
                    {publishingStep > step && <CBadge color="success" className="ml-2">✓</CBadge>}
                  </CNavLink>
                </CNavItem>
              ))}
            </CNav>
            
            <CTabContent className="p-3 border border-top-0 rounded-bottom">
              {/* Step 1: Initialize */}
              <CTabPane visible={publishingStep === 1}>
                <h5>Step 1: Initialize Publishing</h5>
                <p>
                  We'll prepare your skill "{name}" for publishing by authenticating with Amazon.
                </p>
                <CButton color="primary" onClick={handleAuthenticateForPublishing} disabled={isPublishingLoading}>
                  Start Publishing Process
                </CButton>
                {renderPublishingStatusIndicator()}
              </CTabPane>
              
              {/* Step 2: Build Model */}
              <CTabPane visible={publishingStep === 2}>
                <h5>Step 2: Build Interaction Model</h5>
                <p>
                  This step will build the interaction model for your skill.
                  This is required before you can test your skill.
                </p>
                <CButton color="primary" onClick={handleBuildModel} disabled={isPublishingLoading || !accessToken}>
                  Build Interaction Model
                </CButton>
                {renderPublishingStatusIndicator()}
              </CTabPane>
              
              {/* Step 3: Enable Testing */}
              <CTabPane visible={publishingStep === 3}>
                <h5>Step 3: Enable Testing</h5>
                <p>
                  This step will enable testing for your skill.
                  After this, you'll be able to test your skill in the Alexa simulator.
                </p>
                <CButton color="primary" onClick={handleEnableTesting} disabled={isPublishingLoading || !accessToken}>
                  Enable Testing
                </CButton>
                {renderPublishingStatusIndicator()}
              </CTabPane>
              
              {/* Step 4: Submit for Certification */}
              <CTabPane visible={publishingStep === 4}>
                <h5>Step 4: Submit for Certification</h5>
                <p>
                  This step will submit your skill for certification.
                  After certification, your skill will be available to all Alexa users.
                </p>
                <CButton color="primary" onClick={handleSubmitForCertification} disabled={isPublishingLoading || !accessToken}>
                  Submit for Certification
                </CButton>
                {renderPublishingStatusIndicator()}
              </CTabPane>
              
              {/* Step 5: Publish */}
              <CTabPane visible={publishingStep === 5}>
                <h5>Step 5: Publish Skill</h5>
                <p>
                  Your skill has been certified and is now ready to be published to the Alexa Skills Store.
                </p>
                <CButton 
                  color="primary" 
                  onClick={handlePublishSkill} 
                  disabled={isPublishingLoading || 
                           !(certificationStatus === 'CERTIFIED' || publishingStatus === 'CERTIFIED' || 
                             publishingStatus === 'READY_FOR_PUBLICATION' || publishingSuccess.includes('ready for publication'))}
                  className="me-2"
                >
                  {isPublishingLoading ? (
                    <>
                      <CSpinner size="sm" className="me-2" />
                      Publishing...
                    </>
                  ) : certificationStatus === 'IN_PROGRESS' || publishingStatus === 'CERTIFICATION_IN_PROGRESS' ? (
                    "Awaiting Certification"
                  ) : certificationStatus === 'CERTIFIED' || publishingStatus === 'CERTIFIED' || 
                      publishingStatus === 'READY_FOR_PUBLICATION' || publishingSuccess.includes('ready for publication') ? (
                    "Publish Certified Skill"
                  ) : (
                    "Certification Required"
                  )}
                </CButton>
                <CButton 
                  color="success" 
                  onClick={() => window.open('https://developer.amazon.com/alexa/console/ask', '_blank')}
                >
                  Open Developer Console
                </CButton>
                {renderPublishingStatusIndicator()}
              </CTabPane>
              
              {/* Step 6: Status */}
              <CTabPane visible={publishingStep === 6}>
                <h5>Step 6: Publication Status</h5>
                {publicationCheckStatus === 'SUCCEEDED' ? (
                  <CAlert color="success">
                    <strong>Congratulations!</strong> Your skill "{name}" has been successfully published and is now LIVE.
                    {publishesAtDate && ` Published on: ${new Date(publishesAtDate).toLocaleString()}.`}
                  </CAlert>
                ) : publicationCheckStatus === 'FAILED' ? (
                  <CAlert color="danger">
                    <strong>Publication Failed.</strong> Unfortunately, the publication of your skill "{name}" did not succeed.
                    Please check the Amazon Developer Console for more details. Last attempted: {publishesAtDate ? new Date(publishesAtDate).toLocaleString() : 'N/A'}.
                  </CAlert>
                ) : publicationCheckStatus === 'IN_PROGRESS' ? (
                  <CAlert color="info">
                    <strong>Publication In Progress.</strong> Your skill "{name}" is currently being processed by Amazon.
                    Expected completion around: {publishesAtDate ? new Date(publishesAtDate).toLocaleString() : 'N/A'}.
                    Amazon typically takes anywhere from 15 minutes up to 24 hours.
                  </CAlert>
                ) : publicationCheckStatus === 'SCHEDULED' ? (
                  <CAlert color="warning">
                    <strong>Publication Scheduled.</strong> Your skill "{name}" is scheduled for publication.
                    It will go live around: {publishesAtDate ? new Date(publishesAtDate).toLocaleString() : 'N/A'}.
                  </CAlert>
                ) : (
                  <p>
                    The publication status of your skill "{name}" is being checked or has not yet been determined after submission. 
                    Amazon typically takes anywhere from 15 minutes up to 24 hours to complete the publication process once fully submitted and approved. 
                    Click "Refresh Status" to get the latest update.
                  </p>
                )}
                <p>
                  <strong>Skill ID:</strong> {skillId}<br />
                  <strong>Overall Skill Status:</strong> <CBadge color={getBadgeColor(publishingStatus)}>{publishingStatus || 'Unknown'}</CBadge><br />
                  <strong>Certification Status:</strong> <CBadge color={getBadgeColor(certificationStatus)}>{certificationStatus || 'Not Certified'}</CBadge><br />
                  <strong>Detailed Publication Status:</strong> {publicationCheckStatus ? <CBadge color={getBadgeColorForPublication(publicationCheckStatus)}>{publicationCheckStatus}</CBadge> : 'Not yet checked or N/A'}<br />
                  {publishesAtDate && (publicationCheckStatus === 'SCHEDULED' || publicationCheckStatus === 'IN_PROGRESS') && (
                    <><strong>Expected Publication Date:</strong> {new Date(publishesAtDate).toLocaleString()}<br /></>
                  )}
                  {publicationCheckStatus === 'SUCCEEDED' && publishesAtDate && (
                     <><strong>Published At:</strong> {new Date(publishesAtDate).toLocaleString()}<br /></>
                  )}
                </p>
                <CButton color="primary" onClick={() => handleCheckStatus(false)} disabled={isPublishingLoading || !accessToken} className="me-2">
                  Refresh Status
                </CButton>
                <CButton color="success" onClick={() => window.open('https://developer.amazon.com/alexa/console/ask', '_blank')}>
                  Open Developer Console
                </CButton>
                {renderPublishingStatusIndicator()}
              </CTabPane>
            </CTabContent>
          </CCardBody>
        </CCard>
      )}

      <CCard className="mb-4">
        <CCardHeader>
          <h4>Skill Details</h4>
        </CCardHeader>
        <CCardBody>
          <CRow className="mb-4">
            <CCol md={3} className="fw-bold">Skill ID</CCol>
            <CCol md={9}>{skillId}</CCol>
          </CRow>
          
          <CRow className="mb-4">
            <CCol md={3} className="fw-bold">Skill Name</CCol>
            <CCol md={9}>{name}</CCol>
          </CRow>
          
          <CRow className="mb-4">
            <CCol md={3} className="fw-bold">Description</CCol>
            <CCol md={9}>{description}</CCol>
          </CRow>
          
          <CRow className="mb-4">
            <CCol md={3} className="fw-bold">Short Description</CCol>
            <CCol md={9}>{shortDescription}</CCol>
          </CRow>
          
          <CRow className="mb-4">
            <CCol md={3} className="fw-bold">Status</CCol>
            <CCol md={9}>
              <CBadge color={status.toLowerCase() === 'live' ? 'success' : 'primary'}>
                {status}
              </CBadge>
            </CCol>
          </CRow>
          
          <CRow className="mb-4">
            <CCol md={3} className="fw-bold">Invocation Name</CCol>
            <CCol md={9}>{invocationName}</CCol>
          </CRow>
          
          <CRow className="mb-4">
            <CCol md={3} className="fw-bold">Example Phrases</CCol>
            <CCol md={9}>
              {examplePhrases.map((phrase, index) => (
                <div key={index}>{phrase}</div>
              ))}
            </CCol>
          </CRow>
          
          <CRow className="mb-4">
            <CCol md={3} className="fw-bold">Keywords</CCol>
            <CCol md={9}>{Array.isArray(keywords) ? keywords.join(", ") : keywords}</CCol>
          </CRow>
          
          <CRow className="mb-4">
            <CCol md={3} className="fw-bold">Category</CCol>
            <CCol md={9}>{category}</CCol>
          </CRow>
          
          <CRow className="mb-4">
            <CCol md={3} className="fw-bold">Privacy Policy URL</CCol>
            <CCol md={9}>
              {privacyPolicyUrl && (
                <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                  {privacyPolicyUrl}
                </a>
              )}
            </CCol>
          </CRow>
          
          <CRow className="mb-4">
            <CCol md={3} className="fw-bold">Small Icon</CCol>
            <CCol md={9}>
              {smallIconUrl && (
                <img
                  src={smallIconUrl}
                  alt="Small Icon"
                  style={{ maxHeight: 64 }}
                  className="border rounded"
                />
              )}
            </CCol>
          </CRow>
          
          <CRow className="mb-4">
            <CCol md={3} className="fw-bold">Large Icon</CCol>
            <CCol md={9}>
              {largeIconUrl && (
                <img
                  src={largeIconUrl}
                  alt="Large Icon"
                  style={{ maxHeight: 128 }}
                  className="border rounded"
                />
              )}
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default SkillDetail;
